import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, Megaphone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminAnnouncement() {
  const [form, setForm] = useState({
    is_enabled: false,
    message: "",
    link_url: "",
    link_label: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/announcement")
      .then((r) => setForm({
        is_enabled: !!r.data.is_enabled,
        message: r.data.message || "",
        link_url: r.data.link_url || "",
        link_label: r.data.link_label || "",
      }))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load"))
      .finally(() => setLoading(false));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/announcement", form);
      toast.success(form.is_enabled ? "Banner is LIVE!" : "Saved (banner is off)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-paper min-h-[80vh] flex items-center justify-center">
        <p className="text-ink-soft">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-announcement-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="admin-announcement-back-link"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
          Announcement Banner
        </p>
        <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
          The strip at the top of your site
        </h1>
        <p className="text-sm text-ink-soft mt-3 max-w-xl">
          A slim terracotta strip that shows just under the menu on every public
          page. Perfect for &quot;new poem this week&quot;, &quot;book available now&quot;, or a special
          announcement. Readers can tap the share button to spread the word.
        </p>

        {/* Live preview */}
        {form.is_enabled && (form.message || "").trim() && (
          <div className="mt-8">
            <p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-2">Preview</p>
            <div className="bg-terracotta text-paper px-4 py-2.5 flex items-center gap-3 text-sm">
              <Megaphone size={16} className="shrink-0 opacity-90" />
              <p className="flex-1 leading-snug">
                <span className="font-medium tracking-wide">{form.message}</span>
                {form.link_url && form.link_label && (
                  <span className="ml-3 underline font-semibold whitespace-nowrap">
                    {form.link_label} →
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        <div className="mt-10 space-y-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_enabled}
              onChange={(e) => update("is_enabled", e.target.checked)}
              data-testid="announcement-enabled-toggle"
              className="w-5 h-5 mt-1"
            />
            <span>
              <span className="text-sm text-ink font-medium">
                Show announcement banner on the site
              </span>
              <span className="block text-xs text-ink-soft mt-1">
                When OFF, no banner appears anywhere. Toggle ON when you have news.
              </span>
            </span>
          </label>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Message (up to ~2 short sentences)
            </label>
            <textarea
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              data-testid="announcement-message-input"
              rows={2}
              maxLength={280}
              className="w-full bg-paper-2/40 border border-rule focus:border-ink p-3 text-sm text-ink outline-none"
              placeholder="A new poem is up — I hope it meets you where you are."
            />
            <p className="text-[11px] text-ink-soft mt-1">{(form.message || "").length}/280 characters</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
                Link URL (optional)
              </label>
              <input
                value={form.link_url}
                onChange={(e) => update("link_url", e.target.value)}
                data-testid="announcement-link-url-input"
                className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
                placeholder="/writings/my-new-poem"
              />
              <p className="text-[11px] text-ink-soft mt-1 italic">
                Use a page on your site like <code>/writings/lucy</code> or a full link.
              </p>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
                Link Label (optional)
              </label>
              <input
                value={form.link_label}
                onChange={(e) => update("link_label", e.target.value)}
                data-testid="announcement-link-label-input"
                maxLength={40}
                className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
                placeholder="Read now"
              />
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-rule pt-6">
          <button
            onClick={save}
            disabled={saving}
            data-testid="announcement-save-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-terracotta text-paper text-xs tracking-[0.2em] uppercase hover:bg-ink transition-colors disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
